import React, { useState, useEffect } from "react"
import { FormattedMessage, defineMessages, injectIntl } from "react-intl"
import UserEditTabs from "./useredit.component"
import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import SideBar from "../common/layout/sidebar.component"
import IntlProps from "../state/helpers/IntlProps"
import PasswordInput from "../user/form/PasswordInput"
import PasswordStrengthVerificationInput from "./form/PasswordStrengthVerificationInput"
import { GlobalHelper } from "../indexImports"
import PasswordRequirementToolTip from "./form/PasswordRequirementToolTip"
import { match } from "react-router"
import { Alert, Button, Form } from "react-bootstrap"
import PasswordVerificationModal from "./PasswordVerificationModal"

const messages = defineMessages({
    "user.form.edit.password_placeholder": {
        id: "user.form.edit.password_placeholder",
        defaultMessage: "New password of the user",
    },
    "user.form.edit.password_again_placeholder": {
        id: "user.form.edit.password_again_placeholder",
        defaultMessage: "Enter password again",
    },
    "user.form.edit.save_btn": {
        id: "user.form.edit.save_password_btn",
        defaultMessage: "Save",
    },
})

interface Identifiable {
    userId: string
}

type OwnProps = {
    match: match<Identifiable>
}

type FormAttributes = {
    newPassword: string
    passwordAgain: string
}


const PasswordSettingsComponent = (props: OwnProps & IntlProps) => {
    const userId = props.match.params.userId

    const [form, setForm] = useState<FormAttributes>({newPassword: "", passwordAgain: ""})
    const [isPasswordAcceptable, setIsPasswordAcceptable] = useState<boolean>(false)
    const [showVerifyModal, setShowVerifyModal] = useState<boolean>(false)
    const [isSuccess, setIsSuccess] = useState<boolean>(false)

    useEffect(() => {
        SideBar.activateMenu("account")
    }, [])

    const setField = (field, value) => {
        setForm({
            ...form,
            [field]: value
        })
    }

    const getPasswordRules = () => {
        if (!GlobalHelper.checkPasswordRules(form.newPassword)) {
            return < small className={"text-danger mt-1"}>
                <PasswordRequirementToolTip/>
                <FormattedMessage
                    id="user.password_rules.missing_requirements"
                    defaultMessage="Password is missing one or more requirements"
                />
            </small>

        } else if (!isPasswordAcceptable) {
            return <small className={"text-danger mt-1"}>
                <FormattedMessage
                    id="user.password_rules.not_secure_enough"
                    defaultMessage="Password is not secure enough"
                />
            </small>
        }
    }

    const updatePassword = async () => {
        const payload = {
            password: form.newPassword,
            passwordAgain: form.passwordAgain
        }

        const res = await AppContext.getApiClient().patch(`/users/${userId}`, payload)
        if (!res.isError) {
            setIsSuccess(true)
        }
    }

    const isSaveButtonDisabled = form.newPassword !== form.passwordAgain || !form.newPassword || !form.passwordAgain
    const isNotEqual = form.newPassword !== form.passwordAgain
    return (<>
        <UserEditTabs userId={userId}/>
        <div className="row ui-has-modules">
            <div className="col-8">
                <h2 className="m-0 custom-lead">
                    <FormattedMessage
                        id="user.password.settings.component.password_settings"
                        defaultMessage="Password Settings"
                    />
                </h2>
            </div>
        </div>
        <Form>
            {showVerifyModal &&
                    < PasswordVerificationModal
                        userId={userId}
                        password={form.newPassword}
                        passwordAgain={form.passwordAgain}
                        setIsSuccess={setIsSuccess}
                        setShowVerifyModal={setShowVerifyModal}
                    />
            }
            < div className="row ui-has-modules">
                <div className="col-12 ui-module">
                    <Form.Group>
                        <Form.Label htmlFor="password">
                            <FormattedMessage
                                id="user.form.edit.new_password_label"
                                defaultMessage="New Password"
                            />
                        </Form.Label>
                        <PasswordInput
                            inputName="password"
                            handleFormChange={e => setField("newPassword", e.target.value)}
                            value={form.newPassword}
                            placeholder={props.intl.formatMessage(messages["user.form.edit.password_placeholder"])}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label htmlFor="passwordAgain">
                            <FormattedMessage
                                id="user.form.edit.password_again_label"
                                defaultMessage="Password again"
                            />
                        </Form.Label>
                        <PasswordStrengthVerificationInput
                            inputName="passwordAgain"
                            handleFormChange={e => setField("passwordAgain", e.target.value)}
                            handlePasswordScoreChange={(isAcceptable: boolean) => setIsPasswordAcceptable(isAcceptable)}
                            value={form.passwordAgain}
                            placeholder={props.intl.formatMessage(messages["user.form.edit.password_again_placeholder"])}
                            required={true}
                        />
                        {!showVerifyModal && !isSaveButtonDisabled && getPasswordRules()}
                        {isNotEqual && <small className="text-danger mt-1">
                            <FormattedMessage
                                id="user.form.edit.password_update.not_equal_password_error_label"
                                defaultMessage="The passwords don't match"
                            />
                        </small>}
                        <Form.Text className="text-muted">
                            <FormattedMessage
                                id="user.form.editpassword_update.password_again_description"
                                defaultMessage="Enter the password again"
                            />
                        </Form.Text>
                    </Form.Group>
                </div>
            </div>
            <div>
                {isSuccess &&
                        <Alert variant="success">
                            <FormattedMessage
                                id="user.password_edit.success_message"
                                defaultMessage="You have successfully changed your password!"
                            />
                        </Alert>
                }
            </div>
            <div className="col-12 ui-module">
                <div className="row">
                    <div className="col-12 text-align-right">
                        <Button variant="primary"
                            onClick={() =>
                                AppContext.getUserLoggedIn() &&
                                        AppContext.getUserLoggedIn().attributes.superUser ?
                                    updatePassword() :
                                    setShowVerifyModal(isPasswordAcceptable)}
                            disabled={isSaveButtonDisabled || !isPasswordAcceptable}>
                            {props.intl.formatMessage(messages["user.form.edit.save_btn"])}
                        </Button>
                    </div>
                </div>
            </div>
        </Form>
    </>
    )
}

export default injectIntl(PasswordSettingsComponent)


// WEBPACK FOOTER //
// ./src/user/PasswordSettingsComponent.tsx